import { createContext, useContext, useEffect, useState } from 'react';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { useCognito } from 'src/hooks/use-cognito';
import { LoginResult } from 'src/hooks/use-login';
import {
  fetchMonolithJwt,
  removeStoredMonolithJwt,
} from 'src/utils/axios-instance';
import { Unauthorized } from 'src/utils/errors';
import { NoContent } from 'src/utils/no-content';

const url = `${PRISMA_CONFIG.oldBackendUrl}/auth`;

export type MonolithCurrentUserContextValue = LoginResult | null;

const MonolithCurrentUserContext =
  createContext<MonolithCurrentUserContextValue>(null);

export function useMonolithCurrentUser() {
  const context = useContext(MonolithCurrentUserContext);
  if (context === null) {
    throw new Unauthorized();
  }
  return context;
}

export function useOptionalMonolithCurrentUser() {
  return useContext(MonolithCurrentUserContext);
}

export function useMonolithCurrentUserRequest() {
  const [catched, setCatched] = useState(false);
  const cognito = useCognito();

  const auth = useAxios(
    async (axios, signal) => {
      // make two requests in parallel here:
      // 1) we're interested in the /auth response - this data will later
      //    be used in other parts of the app
      // 2) we also trigger an initial request to /auth/token to get a JWT.
      //    that one is not used directly in the app, but only in the interceptors,
      //    so we don't return anything here
      const [auth] = await Promise.all([
        axios
          .request<LoginResult | NoContent>({
            signal,
            url,
            // if the old platform is too slow to respond (>= 10s) a timeout
            // error will be thrown - that way we default to unauthenticated behavior
            // to get at least a working app for every user who wouldn't want to
            // authenticate anyway (and users who want to authenticate would need
            // to use the login)
            timeout: 10000,
          })
          // Special error treatment!
          // This request is done very early in the life cycle of the app.
          // We can't really recover from it as there is nothing in place
          // to recover to. But if we return `null` here, the app at least
          // _starts_ the same as for any unauthenticated user.
          // In case the user just want's to do something where authentication
          // is not needed he/she can happily continue.
          .catch(() => setCatched(true)),
        fetchMonolithJwt(axios)
          // Special error treatment! We just swallow all errors here (except 401s),
          // because it still could be that the token is valid (but the platform is down)
          // and it would be possible for the user to do something meaningful outside
          // of the old platform (like publishing a UMM)
          .catch((error) => {
            if (isServerError(error, 401)) {
              removeStoredMonolithJwt(cognito);
            }
          }),
      ]);

      return auth;
    },
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    auth.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolved = Boolean(auth.response) || catched;
  // if we aren't authenticated, we get "204 No Content" -> empty string
  const value =
    auth.response && auth.response.data !== '' ? auth.response.data : null;

  return [resolved, value] as const;
}

export const MonolithCurrentUserProvider = MonolithCurrentUserContext.Provider;
